<template>

    <div class="quote-content" :class="{ open: isOpen }" role="dialog" aria-modal="false">

        <div class="quote-bar" v-if="!isOpen">
            <div class="flex w-full h-full justify-around items-center">

                <div>
                    {{ $t('quote.bar.quote_to') }}:

                    <template v-if="summaryResult?.cartQuoteSummary.customer">
                        {{ summaryResult?.cartQuoteSummary.customer.billing_address.firstName }} {{ summaryResult?.cartQuoteSummary.customer.billing_address.lastName }}
                    </template>

                    <template v-else>
                        {{ $t('quote.unassigned') }}
                    </template>
                </div>

                <div>{{ $t('quote.bar.products') }}: {{ summaryResult?.cartQuoteSummary.number_of_products }}</div>
                <div>{{ $t('quote.bar.total') }}: {{ getPrice(summaryResult?.cartQuoteSummary.total) }}</div>

                <Button @click="toggle">
                    <template v-if="isOpen">
                        {{ $t('quote.bar.close') }}
                    </template>

                    <template v-else>
                        {{ $t('quote.bar.open') }}
                    </template>
                </Button>
            </div>
        </div>

        <Transition name="fade-up">
            <div v-if="isOpen" class="sheet-content" :style="(!isOpen)?`height: calc(100% - 60px);`:''">
                <QuoteForm
                    :key="customer?.id ?? 'no-customer'"
                    @previewQuote="previewQuote()"
                    @returnToShop="returnToShop()"
                    @customerSelected="(selectedCustomer) => customerSelected(selectedCustomer)"
                    @close="close()"
                    @new="cartQuoteNew()"
                    @save="saveQuote()"
                    @changed="(updateFormData) => saveCart(updateFormData)"
                    :customer="customer"
                    :admin="admin"
                    :calculated="calculated"
                    :currencies="result?.currencyAll"
                    :form="form"
                    :quotePreviewError="quotePreviewError"
                ></QuoteForm>
            </div>
        </Transition>

    </div>

    <Modal ref="previewModal">
        <template #body>
            <iframe :src="previewPdfUrl" width="100%" height="600" style="border: 0"></iframe>
        </template>

        <template #footer="{ closeModal }">
            <div class="flex justify-between">
                <Button @click="closeModal()">{{ $t('common.close') }}</Button>
                <Button @click="openSendModal(closeModal)">{{ $t('quote.send_quote') }}</Button>
            </div>
        </template>
    </Modal>

    <Modal ref="sendModal">
        <template #body>

            <div class="flex flex-col space-y-2">

                <div class="flex flex-col">
                    <Label>{{ $t('quote.email_modal.add_attachment') }}</Label>
                    <input type="file" @input="setAttachmentFile" />
                    <ErrorMessages :errors="emailFormErrors.attachment"></ErrorMessages>
                </div>

                <div class="flex space-x-2">

                    <div class="flex flex-col w-[50%]">
                        <Label>{{ $t('quote.email_modal.send_to') }} *</Label>
                        <Text v-model="emailForm.to"></Text>
                        <ErrorMessages :errors="emailFormErrors.to"></ErrorMessages>
                    </div>

                    <div class="flex flex-col w-[50%]">
                        <Label>{{ $t('quote.email_modal.bcc') }}</Label>
                        <Text v-model="emailForm.bcc"></Text>
                        <ErrorMessages :errors="emailFormErrors.bcc"></ErrorMessages>
                    </div>

                </div>

                <div class="flex flex-col">
                    <Label>{{ $t('quote.email_modal.subject') }} *</Label>
                    <Text v-model="emailForm.subject"></Text>
                    <ErrorMessages :errors="emailFormErrors.subject"></ErrorMessages>
                </div>

                <div class="flex flex-col">
                    <Label>{{ $t('quote.email_modal.body') }} *</Label>
                    <RichTextEditor
                        v-model="emailForm.body"
                        height="300px"
                    ></RichTextEditor>

                    <ErrorMessages :errors="emailFormErrors.body"></ErrorMessages>
                </div>


                <ul>
                    <li><b>[quote.id]</b>: {{ $t('quote.email_modal.keywords.quote_id') }}</li>
                    <li><b>[sender_name]</b>: {{ $t('quote.email_modal.keywords.sender_name') }}</li>
                    <li><b>[configuration.quote_company_name]</b>: {{ $t('quote.email_modal.keywords.sender_company_name') }}</li>
                    <li><b>[customer.first_name]</b>: {{ $t('quote.email_modal.keywords.customer_first_name') }}</li>
                    <li><b>[customer.last_name]</b>: {{ $t('quote.email_modal.keywords.customer_last_name') }}</li>
                </ul>

            </div>

        </template>

        <template #footer="{ closeModal }">
            <div class="flex justify-between">
                <Button @click="closeModal()">{{ $t('common.close') }}</Button>
                <Button @click="sendQuote()">{{ $t('quote.send_quote') }}</Button>
            </div>
        </template>
    </Modal>

</template>

<script setup>
import {ref, watchEffect, watch, onMounted, onUnmounted, inject} from 'vue'

import QuoteForm from "@/components/front/quote/QuoteForm.vue";
import {useMutationForm} from "@/composables/useMutationForm.js";
import {useGlobals, useMutation, useQuery, useLazyQuery, useFormatCurrency} from "@/composables/useGlobals.js";
import cloneDeep from "lodash/cloneDeep";
import { useI18n } from "vue-i18n"
import { useRouterHelper } from "@/composables/useRouterHelper.js";
import {TextArea} from "@jinkosoft/text";

const { getSlug }           = useRouterHelper()

const { locale, messages }  = useI18n()

const abortController       = ref(null)

const calculated            = ref(null)
const customer              = ref(null)
const admin                 = ref(null)

const previewPdfUrl         = ref(null)

const previewModal          = ref(null)
const sendModal             = ref(null)

const settings              = inject('$settings')

const quotePreviewError     = ref(null)

const {
    showError,
    getBackRoute,
    addFlashMessage,
    route,
    router,
    gql
} = useGlobals()

const {
    currency: currentCurrencyCode,
    formatCurrency,
    setFormatCurrency
} = useFormatCurrency(null, "sv-SE");

const isOpen        = ref(false)

const toggle = () => {
    isOpen.value = !isOpen.value

    if(isOpen.value){
        loadQuote() || refetchQuote()
    }

}

const {
    result : summaryResult,
    loading : summaryLoading,
    error : summaryError,
    refetch: refetchQuoteSummary,
} = useQuery(gql`
    query {
        cartQuoteSummary
    }
`, {}, {
    fetchPolicy: 'network-only',
    context: {
        suppressLoading: true,
    },
})

const {
    result,
    loading,
    error,
    load: loadQuote,
    refetch: refetchQuote,
    onResult: onQuoteResult,
} = useLazyQuery(gql`
    query {

        adminProfile {
            meta
        }

        currencyAll

        notificationByKey(key: "quote") {
            subject
            body
        }

        includeMaster

        cartQuote
    }
`, {}, {
    fetchPolicy: 'network-only'
})

const {
    mutate: quoteSave,
    onDone : onQuoteSaveDone,
    onError: onQuoteSaveError
} = useMutation(gql`
    mutation quoteSave($lang: String!, $baseProductUrl: String!, $translations: Json!) {
        includeMaster
        quoteSave(lang: $lang, baseProductUrl: $baseProductUrl, translations: $translations)
    }
`)

const {
    mutate: quotePreview,
    onDone : onQuotePreviewDone,
    onError: onQuotePreviewError
} = useMutation(gql`
    mutation quotePreview($lang: String!, $baseProductUrl: String!, $translations: Json!) {
        includeMaster
        quotePreview(lang: $lang, baseProductUrl: $baseProductUrl, translations: $translations)
    }
`)

const {
    mutate: cartQuoteNew,
    onDone : onCartQuoteNewDone,
    onError: onCartQuoteNewError
} = useMutation(gql`
    mutation cartQuoteNew {
        cartQuoteNew
    }
`)

const {
    mutate: cartQuoteSelectCustomer,
    onDone : onCartQuoteSelectCustomerDone,
    onError: onCartQuoteSelectCustomerError
} = useMutation(gql`
    mutation cartQuoteNew($customerId: ID!, $paymentTerms: Json!) {
        includeMaster
        cartQuoteSelectCustomer(customerId: $customerId, paymentTerms: $paymentTerms)
    }
`)

const { form, errors, loading : formLoading, submit, setForm, stripValueRecursive } = useMutationForm({
    formValues : {
        data        : {},
        items       : [],
    },

    inputKey: "quote",
    resultKey: "cartQuoteSave",
    mutationQuery: gql`
        mutation save($quote: Json!) {
            includeMaster
            cartQuoteSave(quote: $quote)
        }
    `,
    options: {

        onSaved : (data) => {

            calculated.value        = data.calculated
            customer.value          = data.customer

            addFlashMessage({
                type: ["cm-alert-success"],
                message: "Quote has been saved!"
            })
        },

        beforeSubmit : (data) => {
            return data
        }

    },

    transformerCallback: (data) => {
        return data
    }

})

const {
    form : emailForm,
    errors : emailFormErrors,
    loading : emailFormLoading,
    submit : emailFormSubmit,
    setForm : emailSetForm,
} = useMutationForm({
    formValues : {
        attachment  : null,
        to          : null,
        bcc         : null,
        subject     : null,
        body        : null,
    },

    inputKey: "sendQuote",
    resultKey: "quoteSend",
    mutationQuery: gql`
        mutation quoteSend($sendQuote: SendQuoteInput!) {
            includeMaster
            quoteSend(sendQuote: $sendQuote)
        }
    `,
    options: {

        onSaved : (data) => {

            if(data) {

                sendModal.value.close()

                addFlashMessage({
                    type: ["cm-alert-success"],
                    message: "Email has been sent!"
                })

                emailForm.value.attachment  = null
            }

        },

        beforeSubmit : (data) => {
            return data
        }

    },

    transformerCallback: (data) => {
        return data
    }

})

/*
watch(form, (value) => {
    submit()
}, { deep: true })
*/

watch(summaryResult, (value) => {

    if(!value) return

    setFormatCurrency(value.cartQuoteSummary.currency)

})

watch(result, (value) => {

    if(!value) return

    calculated.value        = value.cartQuote.calculated
    customer.value          = value.cartQuote.customer
    admin.value             = value.adminProfile

    emailSetForm({
        to: customer.value?.billing_address?.email ?? "",
        subject: value.notificationByKey?.subject[settings.selectedLanguage] ?? "",
        body: value.notificationByKey?.body[settings.selectedLanguage] ?? "",
    })

    setForm(value.cartQuote.quote)

}, { deep: true })

const customerSelected  = (selectedCustomer) => {
    cartQuoteSelectCustomer({
        customerId: selectedCustomer.id,
        paymentTerms: messages.value[settings.selectedLanguage].quote.payment_terms
    })
}

const saveCart          = async (updateFormData = false) => {

    if (abortController.value) {
        abortController.value.abort()
    }

    abortController.value = new AbortController()

    calculated.value    = null

    const saveCartResult    = await submit({
        updateFormOnSave: false,
        mutateOptions: {
            context: {
                suppressLoading: true,
                fetchOptions: {
                    signal: abortController.value.signal
                }
            }

        }
    })

    if(updateFormData && saveCartResult?.data){
        setForm(saveCartResult.data.cartQuoteSave.quote)
    }



}

const saveQuote         = async () => {

    await quoteSave({
        lang: settings.selectedLanguage,
        baseProductUrl: settings.homeUrl + getSlug(window.jinkosoft_app_data.page_slugs.product, 'product'),
        translations: messages.value[settings.selectedLanguage].quote.pdf
    })

}

const close         = () => {

    toggle()

    refetchQuoteSummary()
}

const previewQuote      = () => {

    console.log(messages.value)

    quotePreview({
        lang: settings.selectedLanguage,
        baseProductUrl: settings.homeUrl + getSlug(window.jinkosoft_app_data.page_slugs.product, 'product'),
        translations: messages.value[settings.selectedLanguage].quote.pdf
    })

}

const sendQuote = async () => {
    await emailFormSubmit()

    isOpen.value     = false

    loadQuote() || refetchQuote()
    refetchQuoteSummary()
}

onQuotePreviewDone((quotePreviewResult) => {

    if(quotePreviewResult.data.quotePreview.error){
        quotePreviewError.value     = quotePreviewResult.data.quotePreview.error
    }else{

        setForm(quotePreviewResult.data.quotePreview.quote)

        previewPdfUrl.value     = quotePreviewResult.data.quotePreview.previewUrl

        previewModal.value.open()

    }
})

onQuoteSaveDone((quoteSaveResult) => {

    setForm(quoteSaveResult.data.quoteSave)

    addFlashMessage({
        type: ["cm-alert-success"],
        message: "Quote has been saved!"
    })
})

onQuoteSaveError((error) => {
    showError("Error: can't save the quote!", error)
})

onCartQuoteNewDone((cartQuoteNewResult) => {

    setForm(cartQuoteNewResult.data.cartQuoteNew)

    customer.value              = null
    calculated.value            = null
    quotePreviewError.value     = null

    addFlashMessage({
        type: ["cm-alert-success"],
        message: "Quote has cleared!"
    })
})

onCartQuoteNewError((error) => {
    showError("Error: can't clear the quote!", error)
})

onCartQuoteSelectCustomerDone((cartQuoteSelectCustomerResult) => {

    setForm(cartQuoteSelectCustomerResult.data.cartQuoteSelectCustomer.quote)

    calculated.value    = cartQuoteSelectCustomerResult.data.cartQuoteSelectCustomer.calculated
    customer.value      = cartQuoteSelectCustomerResult.data.cartQuoteSelectCustomer.customer

    emailForm.value.to  = customer.value?.billing_address?.email ?? ""
    
    addFlashMessage({
        type: ["cm-alert-success"],
        message: "Quote has cleared!"
    })
})

onCartQuoteSelectCustomerError((error) => {
    showError("Error: can't select the customer!", error)
})

const getPrice         = (price, defaultValue = null) => {

    if(currentCurrencyCode.value){
        if(price){

            if(price === '--'){
                return price
            }

            if(price.amount){
                return formatCurrency(price.amount)
            }else{
                return formatCurrency(price)
            }

        }else{
            if(!defaultValue){
                return formatCurrency(0)
            }

            return defaultValue
        }
    }

    return null
}

const returnToShop  = () => {
    router.push({ name: 'catalog' })

    close()
}

const onUpdateSummary       = () => {
    refetchQuoteSummary()
}

const openSendModal     = (closeModalCallback) => {

    closeModalCallback()

    sendModal.value.open()
}

const setAttachmentFile = (event) => {
    const file = event.target.files[0];

    if (file) {
        emailForm.value.attachment = file;
    }

};

onMounted(() => window.addEventListener('quote:update-summary', onUpdateSummary));
onUnmounted(() => window.removeEventListener('quote:update-summary', onUpdateSummary));
</script>

<style lang="scss">

.uk-checkbox {
    border: 1px solid #ccc;
}

.quote-content {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;

    overflow: auto;

    height: 60px;                 // altezza "chiusa"
    background: #fff;             // sfondo del foglio
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    box-shadow: 0 -6px 24px rgba(0,0,0,.12);
    transition: height 300ms ease; // animazione altezza
    will-change: height;
    z-index: 100000;
}

.quote-content.open {
    height: 100vh;                // riempi tutta la viewport -> top "raggiunto"
}

/* Barra in basso dentro al foglio */
.quote-bar {
    height: 60px;
    @apply bg-[#eb7e10];
    display: flex;
    align-items: center;
    padding: 0 12px;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
}

/* Contenuto rivelato */
.sheet-content {
    overflow: auto;
    padding: 16px;
    border-top: 1px solid #eee;
    background: #fff;             // assicurati di avere sfondo chiaro
    color: #111;                  // forza testo scuro (evita eredità di text-white)
}

/* Transizione del contenuto interno (fade/slide) */
.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
.fade-up-enter-active,
.fade-up-leave-active {
    transition: opacity 220ms ease, transform 220ms ease;
}

/* Riduci motion se preferito */
@media (prefers-reduced-motion: reduce) {
    .quote-content { transition: none; }
    .fade-up-enter-active,
    .fade-up-leave-active { transition: none; }
}
</style>
